import type { RoutingPolicyName, RoutingRule } from '@trivio/contracts';
import { providerIdPt } from '../../utils/enums-ptbr';

/** `RoutingPolicy.rules` `maxItems: 50` per the contract (`ROUTING_POLICY_SCHEMA`). */
export const MAX_RULES = 50;

export const ROUTING_POLICY_NAME_PT: Record<RoutingPolicyName, string> = {
  cheapest: 'Mais barato',
  fastest: 'Mais rápido',
  balanced: 'Equilibrado',
};

export function routingPolicyNamePt(name: RoutingPolicyName | string): string {
  return ROUTING_POLICY_NAME_PT[name as RoutingPolicyName] ?? name;
}

/** A brand-new, empty rule for "adicionar regra" — both `match` and `action` start empty; the user fills in at least one field of each before saving (enforced server-side by `validateRoutingPolicy`, surfaced back here as a `422` field error). */
export function emptyRule(): RoutingRule {
  return { match: {}, action: {} };
}

/** Weight is stored/sent in grams (contract convention); shown to the user with a kg conversion as a convenience only — never sent as kg. */
export function weightGramsToKgLabel(grams: number): string {
  return `${(grams / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}kg`;
}

function describeRuleMatch(rule: RoutingRule): string {
  const parts: string[] = [];
  const prefixes = rule.match.to_postal_prefixes;
  if (prefixes && prefixes.length > 0) {
    parts.push(`CEP ${prefixes.join(', ')}`);
  }
  if (rule.match.weight_g_min !== undefined || rule.match.weight_g_max !== undefined) {
    const min = rule.match.weight_g_min !== undefined ? weightGramsToKgLabel(rule.match.weight_g_min) : '0kg';
    const max = rule.match.weight_g_max !== undefined ? weightGramsToKgLabel(rule.match.weight_g_max) : '∞';
    parts.push(`peso ${min}–${max}`);
  }
  return parts.length > 0 ? parts.join(' e ') : 'sem critério definido';
}

function describeRuleAction(rule: RoutingRule): string {
  const parts: string[] = [];
  if (rule.action.policy) {
    parts.push(routingPolicyNamePt(rule.action.policy));
  }
  if (rule.action.force_provider) {
    parts.push(`forçar ${providerIdPt(rule.action.force_provider)}`);
  }
  const excluded = rule.action.exclude_providers;
  if (excluded && excluded.length > 0) {
    parts.push(`excluir ${excluded.map(providerIdPt).join(', ')}`);
  }
  return parts.length > 0 ? parts.join(', ') : 'sem ação definida';
}

/** Textual preview per AC: `"regra 1: CEP 01–19 → mais barato"`. */
export function ruleSummary(rule: RoutingRule, index: number): string {
  return `regra ${index + 1}: ${describeRuleMatch(rule)} → ${describeRuleAction(rule)}`;
}
